import type { FormField, UploadUrlResponse } from '@lucid/lego';
import type { BusinessSearchResult } from '../../api/services/lupp/order/dto.order';
import type { QuoteFileMetadata } from '../../api/services/lupp/quotes/dto.quotes';
import { parseLocalePrice } from '../../utils/tools';
import { isChargingVoucherEnabled } from '../../config/featureFlags';
import {
  QUOTE_FORM_FIELDS,
  DISCOUNT_ENABLED_PAYMENT_METHODS,
  LOAN_PAYMENT_METHODS,
  PARTNER_COMPENSATION_PERCENTAGE,
  AGENT_DISCOUNT_MAX_PERCENTAGE,
  LUCID_DISCOUNT_MAX_PERCENTAGE,
  CHARGING_VOUCHER_VALUE,
  TRADE_IN_SUPPORT_VALUE,
  PAYMENT_METHOD_VALUES,
} from './constants';
import type { IncentiveLimits } from '../../api/services/lupp/incentives/dto.incentives';
import { roundTo2 } from './utils';

/**
 * Labels type for i18n support
 */
type Labels = Record<string, string> | undefined;

/**
 * Default values for the quote form fields
 */
type QuoteFormDefaultValues = {
  /** Customer type (Private/Business) */
  prospectType?: string;
  /** Preferred payment method */
  preferredPaymentMethod?: string;
  /** Expected delivery timeline */
  deliveryTimeline?: string;
  /** Company name (for Business customers) */
  companyName?: string;
  /** Company tax identification number */
  companyTaxIdentification?: string;
  /** Invoice address line 1 */
  invoiceAddressLine1?: string;
  /** City for invoice address */
  invoiceCity?: string;
  invoiceCountry?: string;
  /** Postal code for invoice address */
  invoicePostalCode?: string;
  /** Cash discount amount */
  cashDiscount?: string;
  /** Percentage discount value */
  percentageDiscount?: string;
  /** Agent-funded discount amount */
  agentDiscountAmount?: string;
  /** Agent-funded discount percentage */
  agentDiscountPercentage?: string;
  /** Lucid-funded (campaign flex) discount amount */
  lucidDiscountAmount?: string;
  /** Lucid-funded (campaign flex) discount percentage */
  lucidDiscountPercentage?: string;
  /** Array of requested incentive codes */
  requestedIncentives?:
    | {
        charging_voucher?: boolean;
        trade_in_support?: boolean;
      }
    | string[];
  financeCompanyName?: string;
  financeCompanyAddress?: string;
  financePostalCode?: string;
  financeCity?: string;
  financeCountry?: string;
  financeVat?: string;
  /** Selected finance provider for LFS Lease/Loan quotes */
  financeProvider?: string;
  /** Agent subvention amount for Loan/Leasing payment methods */
  agentSubventionAmount?: string;
  /** Monthly payment including VAT */
  monthlyPaymentInclVat?: string;
  /** Downpayment amount */
  downpayment?: string;
  /** Quote ID from bank quotation */
  quoteId?: string;
  /** Uploaded bank quotation file */
  uploadBankQuotation?: string;
  /** Uploaded quote file metadata from backend */
  quoteFilesMetadata?: QuoteFileMetadata[];
};

const normalizeRequestedIncentives = (
  requestedIncentives?: QuoteFormDefaultValues['requestedIncentives']
): string[] => {
  if (Array.isArray(requestedIncentives)) {
    return requestedIncentives;
  }

  if (!requestedIncentives) {
    return [];
  }

  const normalized: string[] = [];

  if (requestedIncentives.charging_voucher) {
    normalized.push(CHARGING_VOUCHER_VALUE);
  }

  if (requestedIncentives.trade_in_support) {
    normalized.push(TRADE_IN_SUPPORT_VALUE);
  }

  return normalized;
};

/**
 * Options for configuring the quote form behavior
 */
type QuoteFormOptions = {
  incentiveLimits?: IncentiveLimits;
  incentiveUnavailable?: boolean;
  /** Current status of the quote - form is disabled when not 'Active' */
  currentStatus?: string;
  /** Docusign status - form is disabled when 'Shared', 'Sent', or 'DocuSign Sent' */
  docusignStatus?: string;
  /** Deprecated: warning-only discount exceed flag */
  isPercentageDiscountExceed?: boolean;
  /** Deprecated: warning-only max discount value */
  maxDiscountValue?: number;
  /** Deprecated: warning-only cash discount mode flag */
  isCashDiscountUsed?: boolean;
  /** Deprecated: warning-only subvention exceed flag */
  isAgentSubventionExceed?: boolean;
  /** Deprecated: warning-only max subvention value */
  maxAgentSubventionValue?: number;
  /** Currency symbol for locale-aware field labels (default: '€') */
  currencySymbol?: string;
  /** Locale for formatting prices (default: 'en-US') */
  locale?: string;
  /** Whether a company has been selected from D&B search */
  hasCompanySelected?: boolean;
  /** Whether a finance company has been selected from D&B search */
  hasFinanceCompanySelected?: boolean;
  /** Callback when a company selection changes */
  onCompanyChange?: (business: BusinessSearchResult | null) => void;
  /** Callback when a finance company selection changes */
  onFinanceCompanyChange?: (business: BusinessSearchResult | null) => void;
  /** i18n labels from Contentful */
  labels?: Labels;
  /** Quote files metadata for documents list */
  quoteFilesMetadata?: QuoteFileMetadata[];
  /** Finance provider dropdown options, sourced from the FinOps lenders endpoint */
  financeProviderOptions?: { label: string; value: string }[];
  /** Theme for component styling */
  theme?: 'light' | 'dark';
  /** Model year of the vehicle (e.g. '2026') — controls incentive checkbox visibility */
  modelYear?: string;
  /** Whether to show Trade-in Support checkbox (retired for Q3; reinstate for Q4 by passing true) */
  showTradeInSupport?: boolean;
  /** Configuration subtotal (excl. VAT) for cash discount validation */
  configurationSubtotal?: number;
  /** Function to get signed upload URL for file uploads */
  getUploadUrl?: (file: File) => Promise<UploadUrlResponse>;
  /** Callback when a file is removed */
  onFileRemove?: (fileId?: string) => void;
  /** Callback when a document is clicked for download */
  onDocumentClick?: (fileId: string, fileName: string) => void | Promise<void>;
  /** Callback when file upload completes */
  onUploadComplete?: (fileUrl: string, file: File) => void;
  /** Opens the discount guide PDF in a separate tab. */
  onDiscountGuideClick?: () => void;
  isDiscountGuideLoading?: boolean;
  /** Current payment method, including unsaved form selections. */
  preferredPaymentMethod?: string;
};

/** Docusign statuses that should disable the form */
const DOCUSIGN_DISABLED_STATUSES = new Set(['shared', 'sent', 'docusign sent']);

/**
 * Checks if the form should be disabled based on current status or docusign status
 * @param currentStatus - The current status of the quote
 * @param docusignStatus - The docusign status of the quote
 * @returns true if form should be disabled
 */
const isFormDisabled = (currentStatus?: string, docusignStatus?: string): boolean => {
  // Disable if docusign has been sent
  if (docusignStatus && DOCUSIGN_DISABLED_STATUSES.has(docusignStatus.toLowerCase())) {
    return true;
  }
  // If no currentStatus provided, form is enabled (for new quotes)
  if (!currentStatus) return false;
  // Disable form when status is not Active
  return currentStatus !== 'Active';
};

/**
 * Creates a max cap validator for amount fields based on configuration subtotal.
 */
const createDiscountCapAmountValidator = (
  options: QuoteFormOptions | undefined,
  maxPercentage: number = PARTNER_COMPENSATION_PERCENTAGE
) => {
  return (value: unknown): boolean | string => {
    const subtotal = options?.configurationSubtotal ?? 0;
    if (subtotal <= 0) return true;

    const maxAllowedAmount = roundTo2((maxPercentage / 100) * subtotal);
    const numValue = parseLocalePrice(String(value));

    if (numValue > subtotal || numValue > maxAllowedAmount) {
      // Keep field invalid for submit blocking, but render the message as a full-width row.
      return '';
    }

    return true;
  };
};

/**
 * Creates a validator for percentage discount cap.
 */
const createDiscountCapPercentageValidator = (
  maxPercentage: number = PARTNER_COMPENSATION_PERCENTAGE
) => {
  return (value: unknown): boolean | string => {
    const numValue = parseLocalePrice(String(value));
    if (numValue > maxPercentage) {
      // Keep field invalid for submit blocking, but render the message as a full-width row.
      return '';
    }
    return true;
  };
};

/**
 * Form fields configuration for the Create New Quote form
 * @param defaultValues - Optional default values for form fields
 * @param options - Optional configuration options including currentStatus and docusignStatus
 * @returns Array of form field configurations
 */
export const createQuoteFormFields = (
  defaultValues?: QuoteFormDefaultValues,
  options?: QuoteFormOptions
): FormField[] => {
  const disabled = isFormDisabled(options?.currentStatus, options?.docusignStatus);
  const modelYearNumber = Number.parseInt(options?.modelYear || '0', 10);
  // Charging incentives are eligible for MY26 (2026) and later model years.
  const isRequestedIncentivesVisible =
    isChargingVoucherEnabled() && (modelYearNumber === 2026 || modelYearNumber >= 2027);
  const currencySymbol = options?.currencySymbol ?? '€';
  const labels = options?.labels;
  const maximumAgentPercentage =
    options?.incentiveLimits?.maximumAgentDiscountPercentage ??
    (options?.incentiveUnavailable ? 0 : AGENT_DISCOUNT_MAX_PERCENTAGE);
  const maximumLucidPercentage =
    options?.incentiveLimits?.maximumLucidDiscountPercentage ??
    (options?.incentiveUnavailable ? 0 : LUCID_DISCOUNT_MAX_PERCENTAGE);
  const maximumTotalPercentage =
    options?.incentiveLimits?.totalPossibleDiscount ??
    (options?.incentiveUnavailable ? 0 : PARTNER_COMPENSATION_PERCENTAGE);
  const discountNoteContent =
    labels?.['checkout.discountNote.percentage']?.replace(
      /\{\{percentage\}\}/g,
      String(maximumTotalPercentage)
    ) ??
    `Discount limits exceeded: Agent ${maximumAgentPercentage}%, Lucid ${maximumLucidPercentage}%, Total ${maximumTotalPercentage}%. Please contact Lucid Order Ops if a higher discount is required: PartnerSupport-EU@lucidmotors.com.`;
  const isCashPaymentMethod =
    (options?.preferredPaymentMethod ?? defaultValues?.preferredPaymentMethod) === 'Cash';
  const financeGuideHeader = {
    id: 'financeGuideHeader',
    fieldType: 'custom' as const,
    name: 'financeGuideHeader',
    apiKey: 'financeGuideHeader',
    component: 'IncentivesHeader',
    width: 'full' as const,
    visible: !!options?.onDiscountGuideClick,
    componentProps: {
      discountGuideLabel: labels?.['checkout.discountGuide.label'] ?? 'Discount Guide',
      onDiscountGuideClick: options?.onDiscountGuideClick,
      isDiscountGuideLoading: options?.isDiscountGuideLoading,
    },
  };
  const discountDependency = {
    name: QUOTE_FORM_FIELDS.PREFERRED_PAYMENT_METHOD,
    value: DISCOUNT_ENABLED_PAYMENT_METHODS,
  };
  const percentageRangeMessages = {
    min:
      labels?.['checkout.percentageDiscount.validation.min'] ??
      'Please enter a number between 0 and 100',
    max:
      labels?.['checkout.percentageDiscount.validation.max'] ??
      'Please enter a number between 0 and 100',
  };
  return [
    {
      id: 'paymentGroup',
      fieldType: 'group',
      title: '',
      width: 'full',
      variant: 'flat',
      children: [
        {
          id: 'prospectType',
          fieldType: 'dropdown',
          name: 'prospectType',
          apiKey: 'prospectType',
          label: labels?.['checkout.prospectType.label'] ?? 'Customer Type',
          placeholder: labels?.['checkout.prospectType.placeholder'] ?? 'Select customer type',
          required: true,
          width: 'half',
          defaultValue: defaultValues?.prospectType,
          disabled,
          options: [
            {
              label: labels?.['checkout.prospectType.option.private'] ?? 'Private',
              value: 'Private',
            },
            {
              label: labels?.['checkout.prospectType.option.business'] ?? 'Business',
              value: 'Business',
            },
          ],
          validationMessages: {
            required:
              labels?.['checkout.prospectType.validation.required'] ?? 'Customer type is required',
          },
        },
        {
          id: 'preferredPaymentMethod',
          fieldType: 'dropdown',
          name: 'preferredPaymentMethod',
          apiKey: 'preferredPaymentMethod',
          label: labels?.['checkout.preferredPaymentMethod.label'] ?? 'Preferred Payment method',
          placeholder:
            labels?.['checkout.preferredPaymentMethod.placeholder'] ?? 'Select payment method',
          required: true,
          width: 'half',
          defaultValue: defaultValues?.preferredPaymentMethod,
          disabled,
          options: [
            { label: labels?.['form.cash'] ?? 'Cash', value: 'Cash' },
            { label: labels?.['form.lfsLease'] ?? 'LFS Lease', value: 'LFS Lease' },
            { label: labels?.['form.lfsLoan'] ?? 'LFS Loan', value: 'LFS Loan' },
            {
              label: labels?.['form.3rdPartyFinance'] ?? '3rd Party Finance',
              value: '3rd Party Financing',
            },
          ],
          validationMessages: {
            required:
              labels?.['checkout.preferredPaymentMethod.validation.required'] ??
              'Payment method is required',
          },
        },
        {
          id: 'deliveryTimeline',
          fieldType: 'date_picker',
          name: 'deliveryTimeline',
          apiKey: 'deliveryTimeline',
          label: labels?.['checkout.deliveryTimeline.label'] ?? 'Delivery Timeline',
          required: true,
          width: 'full',
          size: 'medium',
          minDate: 'today',
          defaultValue: defaultValues?.deliveryTimeline,
          disabled,
          validationMessages: {
            required:
              labels?.['checkout.deliveryTimeline.validation.required'] ??
              'Delivery timeline is required',
          },
        },
      ],
    },
    {
      id: 'financeProviderGroup',
      fieldType: 'group',
      title: options?.onDiscountGuideClick
        ? undefined
        : (labels?.['checkout.financeProvider.title'] ?? 'Finance Provider Details'),
      width: 'full',
      dependency: {
        name: QUOTE_FORM_FIELDS.PREFERRED_PAYMENT_METHOD,
        value: '3rd Party Financing',
      },
      children: [
        {
          ...financeGuideHeader,
          componentProps: {
            ...financeGuideHeader.componentProps,
            title: labels?.['checkout.financeProvider.title'] ?? 'Finance Provider Details',
          },
        },
        // Finance Company Name field - using custom BusinessNameSearch component
        {
          id: 'financeCompanyName',
          fieldType: 'custom',
          name: 'financeCompanyName',
          apiKey: 'financeCompanyName',
          component: 'BusinessNameSearch',
          required: true,
          width: 'half',
          defaultValue: defaultValues?.financeCompanyName,
          componentProps: {
            label: labels?.['checkout.financeCompany.label'] ?? 'Financial Institution',
            placeholder:
              labels?.['checkout.financeCompany.placeholder'] ??
              'Search for financial institution...',
            required: true,
            disabled,
            dataTestId: 'finance-company-search',
            defaultValue: defaultValues?.financeCompanyName
              ? {
                  duns: '',
                  name: defaultValues.financeCompanyName,
                  address: defaultValues.financeCompanyAddress ?? '',
                  city: defaultValues.financeCity ?? '',
                  postalCode: defaultValues.financePostalCode ?? '',
                  country: defaultValues.financeCountry ?? '',
                  countryCode: '',
                  displayText: defaultValues.financeCompanyName,
                }
              : null,
            onChange: options?.onFinanceCompanyChange,
            messages: {
              searchingText: labels?.['checkout.financeCompany.search.searching'] ?? 'Searching...',
              apiErrorText:
                labels?.['checkout.financeCompany.search.apiError'] ??
                'Search is temporarily unavailable. Please try again later.',
              minCharsText:
                labels?.['checkout.financeCompany.search.minChars'] ??
                'Type at least 3 characters to search',
              noResultsText:
                labels?.['checkout.financeCompany.search.noResults'] ??
                'No matching results found.',
              validationRequiredText:
                labels?.['checkout.financeCompany.search.validationRequired'] ??
                'Please select a valid Financial Institution from the list.',
            },
          },
          validationMessages: {
            required:
              labels?.['checkout.financeCompany.validation.required'] ??
              'Please select a valid Financial Institution from the list.',
          },
        },
        {
          id: 'financeCompanyAddress',
          fieldType: 'text',
          name: 'financeCompanyAddress',
          apiKey: 'financeCompanyAddress',
          label: labels?.['checkout.financeCompanyAddress.label'] ?? 'Address',
          placeholder: labels?.['checkout.financeCompanyAddress.placeholder'] ?? 'Enter Address',
          required: true,
          width: 'full',
          disabled: disabled || options?.hasFinanceCompanySelected, // Disable when finance company selected
          defaultValue: defaultValues?.financeCompanyAddress,
          validationMessages: {
            required:
              labels?.['checkout.financeCompanyAddress.validation.required'] ??
              'Address is required',
          },
        },
        {
          id: 'financePostalCode',
          fieldType: 'text',
          name: 'financePostalCode',
          apiKey: 'financePostalCode',
          label: labels?.['checkout.financePostalCode.label'] ?? 'Postal Code',
          placeholder: labels?.['checkout.financePostalCode.placeholder'] ?? 'Enter Postal Code',
          required: true,
          width: 'third',
          disabled: disabled || options?.hasFinanceCompanySelected, // Disable when finance company selected
          defaultValue: defaultValues?.financePostalCode,
          postalCodeAutoPopulate: {
            cityField: 'financeCity',
            countryField: 'financeCountry',
          },
          validationMessages: {
            required:
              labels?.['checkout.financePostalCode.validation.required'] ??
              'Postal code is required',
          },
        },
        {
          id: 'financeCity',
          fieldType: 'text',
          name: 'financeCity',
          apiKey: 'financeCity',
          label: labels?.['checkout.financeCity.label'] ?? 'City',
          placeholder: labels?.['checkout.financeCity.placeholder'] ?? 'Enter City',
          required: true,
          width: 'third',
          disabled: disabled || options?.hasFinanceCompanySelected, // Disable when finance company selected
          defaultValue: defaultValues?.financeCity,
          validationMessages: {
            required: labels?.['checkout.financeCity.validation.required'] ?? 'City is required',
          },
        },
        {
          id: 'financeCountry',
          fieldType: 'text',
          name: 'financeCountry',
          apiKey: 'financeCountry',
          label: labels?.['checkout.financeCountry.label'] ?? 'Country',
          placeholder: labels?.['checkout.financeCountry.placeholder'] ?? 'Enter Country',
          required: true,
          width: 'third',
          disabled: disabled || options?.hasFinanceCompanySelected, // Disable when finance company selected
          defaultValue: defaultValues?.financeCountry,
          validationMessages: {
            required:
              labels?.['checkout.financeCountry.validation.required'] ?? 'Country is required',
          },
        },
        {
          id: 'financeVat',
          fieldType: 'number',
          name: 'financeVat',
          apiKey: 'financeVat',
          label: labels?.['checkout.financeVat.label'] ?? 'VAT#',
          placeholder: labels?.['checkout.financeVat.placeholder'] ?? 'Enter VAT#',
          required: false,
          width: 'half',
          disabled,
          defaultValue: defaultValues?.financeVat,
        },
      ],
    },
    {
      id: 'financeProviderDetailsGroup',
      fieldType: 'group',
      title: options?.onDiscountGuideClick
        ? undefined
        : (labels?.['checkout.financeProviderDetails.title'] ?? 'Finance Provider Details'),
      width: 'full',
      dependency: {
        name: QUOTE_FORM_FIELDS.PREFERRED_PAYMENT_METHOD,
        value: LOAN_PAYMENT_METHODS,
      },
      children: [
        {
          ...financeGuideHeader,
          id: 'lfsFinanceGuideHeader',
          name: 'lfsFinanceGuideHeader',
          apiKey: 'lfsFinanceGuideHeader',
          componentProps: {
            ...financeGuideHeader.componentProps,
            title: labels?.['checkout.financeProviderDetails.title'] ?? 'Finance Provider Details',
          },
        },
        {
          id: 'financeProvider',
          fieldType: 'dropdown',
          name: 'financeProvider',
          apiKey: 'financeProvider',
          label: labels?.['checkout.financeProvider.label'] ?? 'Finance Provider',
          placeholder:
            labels?.['checkout.financeProvider.placeholder'] ?? 'Select finance provider',
          // Optional when FinOps returns no lenders, so LFS quotes can still be saved.
          required: (options?.financeProviderOptions?.length ?? 0) > 0,
          width: 'full',
          disabled,
          options: options?.financeProviderOptions ?? [],
          defaultValue: defaultValues?.financeProvider,
          validationMessages: {
            required:
              labels?.['checkout.financeProvider.validation.required'] ??
              'Finance provider is required',
          },
        },
      ],
    },
    {
      id: 'businessInformationGroup',
      fieldType: 'group',
      title: labels?.['checkout.businessInformation.title'] ?? 'Business Information',
      width: 'full',
      dependency: {
        name: QUOTE_FORM_FIELDS.PROSPECT_TYPE,
        value: 'Business',
      },
      children: [
        // Company Name field - using custom BusinessNameSearch component
        {
          id: 'companyName',
          fieldType: 'custom',
          name: 'companyName',
          apiKey: 'companyName',
          component: 'BusinessNameSearch',
          required: true,
          width: 'half',
          defaultValue: defaultValues?.companyName,
          componentProps: {
            label: labels?.['checkout.companyName.label'] ?? 'Company Name',
            placeholder: labels?.['checkout.companyName.placeholder'] ?? 'Search for company...',
            required: true,
            disabled,
            dataTestId: 'company-name-search',
            defaultValue: defaultValues?.companyName
              ? {
                  duns: '',
                  name: defaultValues.companyName,
                  address: defaultValues.invoiceAddressLine1 ?? '',
                  city: defaultValues.invoiceCity ?? '',
                  postalCode: defaultValues.invoicePostalCode ?? '',
                  country: defaultValues.invoiceCountry ?? '',
                  countryCode: '',
                  displayText: defaultValues.companyName,
                }
              : null,
            onChange: options?.onCompanyChange,
            messages: {
              searchingText: labels?.['checkout.companyName.search.searching'] ?? 'Searching...',
              apiErrorText:
                labels?.['checkout.companyName.search.apiError'] ??
                'Search is temporarily unavailable. Please try again later.',
              minCharsText:
                labels?.['checkout.companyName.search.minChars'] ??
                'Type at least 3 characters to search',
              noResultsText:
                labels?.['checkout.companyName.search.noResults'] ?? 'No matching results found.',
              validationRequiredText:
                labels?.['checkout.companyName.search.validationRequired'] ??
                'Please select a valid Company from the list.',
            },
          },
          validationMessages: {
            required:
              labels?.['checkout.companyName.validation.required'] ??
              'Please select a valid Company from the list.',
          },
        },
        {
          id: 'companyTaxId',
          fieldType: 'text',
          name: 'companyTaxId',
          apiKey: 'companyTaxId',
          label: labels?.['checkout.companyTaxId.label'] ?? 'Company Tax ID',
          placeholder: labels?.['checkout.companyTaxId.placeholder'] ?? 'Enter Company Tax ID',
          defaultValue: defaultValues?.companyTaxIdentification,
          required: false,
          width: 'half',
          disabled,
        },
        {
          id: 'invoiceAddress',
          fieldType: 'text',
          name: 'invoiceAddress',
          apiKey: 'invoiceAddress',
          label: labels?.['checkout.invoiceAddress.label'] ?? 'Invoice Address',
          placeholder: labels?.['checkout.invoiceAddress.placeholder'] ?? 'Enter Invoice Address',
          required: true,
          width: 'full',
          defaultValue: defaultValues?.invoiceAddressLine1,
          disabled: disabled || options?.hasCompanySelected, // Disable when company selected
          validationMessages: {
            required:
              labels?.['checkout.invoiceAddress.validation.required'] ??
              'Invoice address is required',
          },
        },
        {
          id: 'businessPostalCode',
          fieldType: 'text',
          name: 'businessPostalCode',
          apiKey: 'businessPostalCode',
          label: labels?.['checkout.businessPostalCode.label'] ?? 'Postal Code',
          placeholder: labels?.['checkout.businessPostalCode.placeholder'] ?? 'Enter Postal Code',
          required: true,
          width: 'third',
          defaultValue: defaultValues?.invoicePostalCode,
          disabled: disabled || options?.hasCompanySelected, // Disable when company selected
          postalCodeAutoPopulate: {
            cityField: 'businessCity',
            countryField: 'invoiceCountry',
          },
          validationMessages: {
            required:
              labels?.['checkout.businessPostalCode.validation.required'] ??
              'Postal code is required',
          },
        },
        {
          id: 'businessCity',
          fieldType: 'text',
          name: 'businessCity',
          apiKey: 'businessCity',
          label: labels?.['checkout.businessCity.label'] ?? 'City',
          placeholder: labels?.['checkout.businessCity.placeholder'] ?? 'Enter City',
          required: true,
          width: 'third',
          defaultValue: defaultValues?.invoiceCity,
          disabled: disabled || options?.hasCompanySelected, // Disable when company selected
          validationMessages: {
            required: labels?.['checkout.businessCity.validation.required'] ?? 'City is required',
          },
        },
        {
          id: 'invoiceCountry',
          fieldType: 'text',
          name: 'invoiceCountry',
          apiKey: 'invoiceCountry',
          label: labels?.['checkout.invoiceCountry.label'] ?? 'Country',
          placeholder: labels?.['checkout.invoiceCountry.placeholder'] ?? 'Enter Country',
          required: true,
          width: 'third',
          defaultValue: defaultValues?.invoiceCountry,
          disabled: disabled || options?.hasCompanySelected, // Disable when company selected
          validationMessages: {
            required:
              labels?.['checkout.invoiceCountry.validation.required'] ?? 'Country is required',
          },
        },
      ],
    },
    {
      id: 'quotationDetailsGroup',
      fieldType: 'group',
      title: labels?.['checkout.quotationDetails.title'] ?? 'Quotation Details',
      width: 'full',
      dependency: {
        name: QUOTE_FORM_FIELDS.PREFERRED_PAYMENT_METHOD,
        value: LOAN_PAYMENT_METHODS,
      },
      children: [
        {
          id: 'uploadBankQuotation',
          fieldType: 'custom',
          name: 'uploadBankQuotation',
          apiKey: 'uploadBankQuotation',
          component: 'FileUpload',
          width: 'full',
          componentProps: {
            // Form inverts the theme, so we match that behavior
            theme: options?.theme === 'dark' ? 'light' : 'dark',
            label: labels?.['checkout.uploadBankQuotation.label'] ?? 'Upload Bank Quotation',
            helperText:
              labels?.['checkout.uploadBankQuotation.helperText'] ??
              'PDF or image files supported (max 10 MB)',
            dropzoneText:
              labels?.['checkout.uploadBankQuotation.dropzoneText'] ??
              'Drag and drop files here, or click to select',
            accept: '.pdf,.jpg,.jpeg,.png',
            maxFileSize: 10485760,
            multiple: false,
            disabled,
            dataTestId: 'checkout-bank-quotation-upload',
            initialFiles: (() => {
              const files = options?.quoteFilesMetadata || [];
              if (files.length === 0) return [];
              // Take the last file (most recently added)
              const latestFile = files[files.length - 1];
              if (!latestFile) return [];
              return [
                {
                  fileId: latestFile.fileId,
                  fileName: latestFile.fileNameOriginal,
                  mimeType: latestFile.mimeType,
                },
              ];
            })(),
            getUploadUrl: options?.getUploadUrl,
            onFileRemove: options?.onFileRemove,
            onDocumentClick: options?.onDocumentClick,
            onUploadComplete: options?.onUploadComplete,
          },
        },
        {
          id: 'quoteId',
          fieldType: 'text',
          name: 'quoteId',
          apiKey: 'quoteId',
          label: labels?.['checkout.quoteId.label'] ?? 'Quote ID',
          placeholder: labels?.['checkout.quoteId.placeholder'] ?? 'Enter Quote ID',
          required: false,
          width: 'half',
          disabled,
          defaultValue: defaultValues?.quoteId,
          pattern: /^[a-zA-Z0-9-]*$/,
          validationMessages: {
            pattern:
              labels?.['checkout.quoteId.validation'] ??
              'Only alphanumeric characters and hyphens are allowed',
          },
        },
        {
          id: 'agentSubventionAmount',
          fieldType: 'currency',
          name: QUOTE_FORM_FIELDS.AGENT_SUBVENTION_AMOUNT,
          apiKey: QUOTE_FORM_FIELDS.AGENT_SUBVENTION_AMOUNT,
          label:
            labels?.['checkout.agentSubventionAmount.label']?.replace(
              '{{currency}}',
              currencySymbol
            ) ?? `Agent Subvention Amount (${currencySymbol})`,
          placeholder: labels?.['checkout.agentSubventionAmount.placeholder'] ?? 'Enter amount',
          required: true,
          width: 'full',
          disabled,
          defaultValue: defaultValues?.agentSubventionAmount,
          decimalPlaces: 2,
          validate: createDiscountCapAmountValidator(options, maximumTotalPercentage),
          validationMessages: {
            required:
              labels?.['checkout.agentSubventionAmount.validation.required'] ??
              'Agent subvention amount is required',
          },
        },
        {
          id: 'subventionCapError',
          fieldType: 'content',
          visible: options?.isAgentSubventionExceed,
          content: discountNoteContent,
          variant: 'body',
          size: 'small',
          color: 'error-1',
          width: 'full',
        },
        {
          id: 'monthlyPaymentInclVat',
          fieldType: 'currency',
          name: 'monthlyPaymentInclVat',
          apiKey: 'monthlyPaymentInclVat',
          label:
            labels?.['checkout.monthlyPayment.label']?.replace('{{currency}}', currencySymbol) ??
            `Monthly Payment Incl. VAT (${currencySymbol})`,
          placeholder: labels?.['checkout.monthlyPayment.placeholder'] ?? 'Enter monthly payment',
          required: true,
          width: 'half',
          disabled,
          defaultValue: defaultValues?.monthlyPaymentInclVat,
          decimalPlaces: 2,
          validationMessages: {
            required:
              labels?.['checkout.monthlyPayment.validation.required'] ??
              'Monthly payment is required',
          },
        },
        {
          id: 'downpayment',
          fieldType: 'currency',
          name: 'downpayment',
          apiKey: 'downpayment',
          label:
            labels?.['checkout.downpayment.label']?.replace('{{currency}}', currencySymbol) ??
            `Downpayment (${currencySymbol})`,
          placeholder: labels?.['checkout.downpayment.placeholder'] ?? 'Enter downpayment',
          required: false,
          width: 'full',
          disabled,
          defaultValue: defaultValues?.downpayment,
          decimalPlaces: 2,
        },
      ],
    },
    {
      id: 'incentivesGroup',
      fieldType: 'group',
      title: options?.onDiscountGuideClick
        ? undefined
        : (labels?.['checkout.incentives.title'] ?? 'Discounts'),
      width: 'full',
      dependency: {
        name: QUOTE_FORM_FIELDS.PREFERRED_PAYMENT_METHOD,
        value: isRequestedIncentivesVisible
          ? PAYMENT_METHOD_VALUES
          : DISCOUNT_ENABLED_PAYMENT_METHODS,
      },
      children: [
        {
          id: 'incentivesHeader',
          fieldType: 'custom',
          name: 'incentivesHeader',
          apiKey: 'incentivesHeader',
          component: 'IncentivesHeader',
          width: 'full',
          visible: !!options?.onDiscountGuideClick,
          componentProps: {
            title: labels?.['checkout.incentives.title'] ?? 'Discounts',
            discountGuideLabel: labels?.['checkout.discountGuide.label'] ?? 'Discount Guide',
            onDiscountGuideClick: isCashPaymentMethod ? options?.onDiscountGuideClick : undefined,
            isDiscountGuideLoading: options?.isDiscountGuideLoading,
          },
        },
        {
          id: 'requestedIncentives',
          fieldType: 'checkbox_group',
          name: 'requestedIncentives',
          apiKey: 'requestedIncentives',
          visible: isRequestedIncentivesVisible,
          label: labels?.['checkout.requestedIncentives.label'] ?? 'Requested Incentives',
          width: 'full',
          layout: 'vertical',
          required: false,
          disabled,
          defaultValue: normalizeRequestedIncentives(defaultValues?.requestedIncentives),
          options: [
            // Q4: UNCOMMENT Trade-in Support option below to enable for Q4
            // ...(options?.showTradeInSupport
            //   ? [
            //       {
            //         value: 'trade_in_support',
            //         label:
            //           labels?.['checkout.requestedIncentives.option.tradeInSupport'] ??
            //           'Trade-in Support',
            //         tooltipText:
            //           labels?.['checkout.requestedIncentives.option.tradeInSupport.tooltip'] ??
            //           'Used to convince customers to trade a competitor brand for a Lucid. To support, Lucid will add a pre-defined amount to the trade-in value of a customers vehicle.',
            //       },
            //     ]
            //   : []),
            ...(isRequestedIncentivesVisible
              ? [
                  {
                    value: CHARGING_VOUCHER_VALUE,
                    label:
                      labels?.['checkout.requestedIncentives.option.chargingCreditOffer'] ??
                      'Charging Credit Offer',
                    tooltipText:
                      labels?.['checkout.requestedIncentives.option.chargingCreditOffer.tooltip'] ??
                      'Charging credit offer applied to eligible model years.',
                  },
                ]
              : []),
          ],
          validationMessages: {
            required:
              labels?.['checkout.requestedIncentives.validation.required'] ??
              'Requested incentives is required',
          },
        },
        {
          id: 'lucidDiscountAmount',
          fieldType: 'currency',
          name: QUOTE_FORM_FIELDS.LUCID_DISCOUNT_AMOUNT,
          apiKey: QUOTE_FORM_FIELDS.LUCID_DISCOUNT_AMOUNT,
          label:
            labels?.['checkout.campaignFlexDiscountAmount.label'] ??
            'Campaign Flex Discount Amount',
          placeholder: labels?.['checkout.discountAmount.placeholder'] ?? 'Enter amount',
          required: false,
          width: 'half',
          defaultValue: defaultValues?.lucidDiscountAmount,
          disabled,
          dependency: discountDependency,
          disabledDependency: { name: QUOTE_FORM_FIELDS.LUCID_DISCOUNT_PERCENTAGE },
          decimalPlaces: 2,
          validate: createDiscountCapAmountValidator(options, maximumLucidPercentage),
        },
        {
          id: 'lucidDiscountPercentage',
          fieldType: 'currency',
          name: QUOTE_FORM_FIELDS.LUCID_DISCOUNT_PERCENTAGE,
          apiKey: QUOTE_FORM_FIELDS.LUCID_DISCOUNT_PERCENTAGE,
          label:
            labels?.['checkout.campaignFlexDiscountPercentage.label'] ?? 'Campaign Flex Discount %',
          placeholder: labels?.['checkout.discountPercentage.placeholder'] ?? 'Enter percentage',
          required: false,
          width: 'half',
          defaultValue: defaultValues?.lucidDiscountPercentage,
          disabled,
          dependency: discountDependency,
          disabledDependency: { name: QUOTE_FORM_FIELDS.LUCID_DISCOUNT_AMOUNT },
          decimalPlaces: 2,
          min: 0,
          max: 100,
          validate: createDiscountCapPercentageValidator(maximumLucidPercentage),
          validationMessages: percentageRangeMessages,
        },
        {
          id: 'agentDiscountAmount',
          fieldType: 'currency',
          name: QUOTE_FORM_FIELDS.AGENT_DISCOUNT_AMOUNT,
          apiKey: QUOTE_FORM_FIELDS.AGENT_DISCOUNT_AMOUNT,
          label: labels?.['checkout.agentDiscountAmount.label'] ?? 'Agent Discount Amount',
          placeholder: labels?.['checkout.discountAmount.placeholder'] ?? 'Enter amount',
          required: false,
          width: 'half',
          defaultValue: defaultValues?.agentDiscountAmount,
          disabled,
          dependency: discountDependency,
          disabledDependency: { name: QUOTE_FORM_FIELDS.AGENT_DISCOUNT_PERCENTAGE },
          decimalPlaces: 2,
          validate: createDiscountCapAmountValidator(options, maximumAgentPercentage),
        },
        {
          id: 'agentDiscountPercentage',
          fieldType: 'currency',
          name: QUOTE_FORM_FIELDS.AGENT_DISCOUNT_PERCENTAGE,
          apiKey: QUOTE_FORM_FIELDS.AGENT_DISCOUNT_PERCENTAGE,
          label: labels?.['checkout.agentDiscountPercentage.label'] ?? 'Agent Discount %',
          placeholder: labels?.['checkout.discountPercentage.placeholder'] ?? 'Enter percentage',
          required: false,
          width: 'half',
          defaultValue: defaultValues?.agentDiscountPercentage,
          disabled,
          dependency: discountDependency,
          disabledDependency: { name: QUOTE_FORM_FIELDS.AGENT_DISCOUNT_AMOUNT },
          decimalPlaces: 2,
          min: 0,
          max: 100,
          validate: createDiscountCapPercentageValidator(maximumAgentPercentage),
          validationMessages: percentageRangeMessages,
        },
        {
          id: 'cashDiscount',
          fieldType: 'currency',
          name: QUOTE_FORM_FIELDS.CASH_DISCOUNT,
          apiKey: QUOTE_FORM_FIELDS.CASH_DISCOUNT,
          label: labels?.['checkout.totalDiscountAmount.label'] ?? 'Total Discount Amount',
          required: false,
          width: 'half',
          defaultValue: defaultValues?.cashDiscount,
          // Read-only subtotal computed from agent + lucid amounts
          disabled: true,
          dependency: discountDependency,
          decimalPlaces: 2,
          validate: createDiscountCapAmountValidator(options, maximumTotalPercentage),
        },
        {
          id: 'percentageDiscount',
          fieldType: 'currency',
          name: QUOTE_FORM_FIELDS.PERCENTAGE_DISCOUNT,
          apiKey: QUOTE_FORM_FIELDS.PERCENTAGE_DISCOUNT,
          label: labels?.['checkout.totalDiscountPercentage.label'] ?? 'Total Discount %',
          required: false,
          width: 'half',
          disabled: true,
          dependency: discountDependency,
          defaultValue: defaultValues?.percentageDiscount,
          decimalPlaces: 2,
          validate: createDiscountCapPercentageValidator(maximumTotalPercentage),
        },
        {
          id: 'discountCampaignNote',
          fieldType: 'content',
          content:
            labels?.['checkout.discountCampaignNote'] ??
            'Lucid Discounts must comply with the applicable quarterly sales campaign.',
          variant: 'body',
          size: 'small',
          width: 'full',
        },
        {
          id: 'discountCapError',
          fieldType: 'content',
          visible: options?.isPercentageDiscountExceed,
          content: discountNoteContent,
          variant: 'body',
          size: 'small',
          color: 'error-1',
          width: 'full',
        },
      ],
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      align: 'spaceBetween',
      buttons: [
        {
          id: 'submitButton',
          label: labels?.['checkout.saveQuote'] ?? 'Save Quote',
          type: 'primary',
          size: 'medium',
          color: 'neutral',
          theme: 'dark',
          isFluid: true,
          disabled: disabled || options?.incentiveUnavailable,
        },
      ],
    },
  ];
};
